(() => {
  if (window.DOCollectionCart) return;

  const STORAGE_KEY = 'doapparel:collection-carts:v1';
  const CHANGE_EVENT = 'collection-cart:change';

  class CollectionCartManager {
    constructor() {
      this.state = this._readState();
      this._syncPromise = null;
      this._syncedHandle = '';
    }

    _readState() {
      try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        return {
          activeHandle: typeof raw.activeHandle === 'string' ? raw.activeHandle : '',
          carts: raw.carts && typeof raw.carts === 'object' ? raw.carts : {},
          cartToken: typeof raw.cartToken === 'string' ? raw.cartToken : '',
          syncedHandle: typeof raw.syncedHandle === 'string' ? raw.syncedHandle : '',
        };
      } catch {
        return { activeHandle: '', carts: {}, cartToken: '', syncedHandle: '' };
      }
    }

    _writeState() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } catch {}
    }

    _emitChange(handle) {
      window.dispatchEvent(
        new CustomEvent(CHANGE_EVENT, {
          detail: { handle: handle || this.getActiveHandle() },
        })
      );
    }

    getActiveHandle() {
      return this.state.activeHandle || '';
    }

    setActiveCollection(handle, title) {
      const h = String(handle || '').trim();
      if (!h) return;
      this.state.activeHandle = h;
      this._ensureCart(h);
      if (title) this.state.carts[h].title = String(title);
      this._writeState();
    }

    _ensureCart(handle) {
      if (!this.state.carts[handle]) {
        this.state.carts[handle] = { title: '', items: [] };
      }
      const cart = this.state.carts[handle];
      if (!Array.isArray(cart.items)) cart.items = [];
      return cart;
    }

    getCart(handle) {
      const h = String(handle || this.getActiveHandle()).trim();
      if (!h) return { handle: '', title: '', items: [], itemCount: 0 };
      const cart = this._ensureCart(h);
      const itemCount = cart.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      return { handle: h, title: cart.title || '', items: cart.items, itemCount };
    }

    saveFromShopifyCart(shopifyCart, handle) {
      const h = String(handle || this.getActiveHandle()).trim();
      if (!h || !shopifyCart?.items) return;
      const cart = this._ensureCart(h);
      cart.items = shopifyCart.items.map((item) => ({
        id: Number(item.variant_id || item.id),
        quantity: Number(item.quantity) || 0,
        properties: this._cloneProperties(item.properties),
      }));
      if (shopifyCart.token) {
        this.state.cartToken = this._normalizeToken(shopifyCart.token);
      }
      this.state.syncedHandle = h;
      this._writeState();
      this._emitChange(h);
    }

    clearCart(handle) {
      const h = String(handle || this.getActiveHandle()).trim();
      if (!h) return;
      const cart = this._ensureCart(h);
      if (cart.items.length === 0) return;
      cart.items = [];
      this._writeState();
      this._emitChange(h);
    }

    invalidateSync() {
      this._syncedHandle = '';
    }

    // Re-read localStorage to refresh state frozen by a bfcache restore.
    reloadState() {
      this.state = this._readState();
      this._syncedHandle = '';
    }

    _normalizeToken(token) {
      return String(token || '').split('?')[0];
    }

    _getCookieCartToken() {
      const match = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
      if (!match) return '';
      try {
        return this._normalizeToken(decodeURIComponent(match[1]));
      } catch {
        return '';
      }
    }

    // Synchronous variant of the order-completion check: the `cart` cookie holds
    // the cart token and is removed by Shopify once a checkout consumes the cart.
    // Lets us drop stale local items before the first badge paint, with no network.
    reconcileFromCookie() {
      const storedToken = this.state.cartToken;
      if (!storedToken) return false;

      const cookieToken = this._getCookieCartToken();
      if (cookieToken === storedToken) return false;

      const consumedHandle = this.state.syncedHandle || this.getActiveHandle();
      if (consumedHandle) this.clearCart(consumedHandle);

      this.state.cartToken = cookieToken;
      this._writeState();
      return true;
    }

    _cloneProperties(props) {
      if (!props || typeof props !== 'object') return {};
      const result = {};
      for (const [key, value] of Object.entries(props)) {
        if (key && value != null && value !== '') {
          result[key] = String(value);
        }
      }
      return result;
    }

    toShopifyItems(handle) {
      const h = String(handle || this.getActiveHandle()).trim();
      if (!h) return [];
      const cart = this._ensureCart(h);
      return cart.items
        .filter((item) => item.id && (Number(item.quantity) || 0) > 0)
        .map((item) => ({
          id: Number(item.id),
          quantity: Number(item.quantity) || 1,
          properties: item.properties || {},
        }));
    }

    async ensureShopifySync(handle) {
      const h = String(handle || this.getActiveHandle()).trim();
      if (!h) return;
      if (this._syncedHandle === h) return;
      if (this._syncPromise) return this._syncPromise;

      this._syncPromise = this._syncToShopify(h)
        .then(() => {
          this._syncedHandle = h;
        })
        .finally(() => {
          this._syncPromise = null;
        });
      return this._syncPromise;
    }

    // After a completed checkout Shopify issues a new cart token with an empty cart.
    // Detect that and drop the local copy of the consumed cart, otherwise the sync
    // below would re-add already ordered items back into the Shopify cart.
    async _reconcileCompletedOrder(root) {
      try {
        const response = await fetch(`${root}cart.js`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) return;
        const shopifyCart = await response.json();
        const currentToken = this._normalizeToken(shopifyCart.token);
        const storedToken = this.state.cartToken;

        if (storedToken && currentToken && currentToken !== storedToken && shopifyCart.item_count === 0) {
          const consumedHandle = this.state.syncedHandle || this.getActiveHandle();
          if (consumedHandle) this.clearCart(consumedHandle);
        }

        if (currentToken && currentToken !== storedToken) {
          this.state.cartToken = currentToken;
          this._writeState();
        }
      } catch {}
    }

    async _syncToShopify(handle) {
      const root = window.Shopify?.routes?.root ?? '/';

      await this._reconcileCompletedOrder(root);

      await fetch(`${root}cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });

      this.state.syncedHandle = handle;
      this._writeState();

      const items = this.toShopifyItems(handle);
      if (items.length === 0) return;

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        for (const item of items) {
          try {
            await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ items: [item] }),
            });
          } catch {}
        }

        try {
          const cartResponse = await fetch(`${root}cart.js`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (cartResponse.ok) {
            this.saveFromShopifyCart(await cartResponse.json(), handle);
          }
        } catch {}
      }
    }
  }

  window.DOCollectionCart = new CollectionCartManager();
})();
